/**
 * One exception: the record, who has signed, what reviews found, and what
 * can be done to it next.
 *
 * The actions are many because the lifecycle is; only the ones this person
 * may take now are shown (exceptionActions). Anything that needs a word —
 * a chain, a comment, a review, a new date, a confirmation — opens in place
 * under the buttons.
 */

import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useTaxonomy } from '../admin/useTaxonomy';
import { useMembers } from '../admin/useMembers';
import { isCallFailure } from '../../lib/callable';
import { EXC_ASSESSMENT_MAX, EXC_COMMENT_MAX } from '../../types/fieldLimits';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import { BackLink } from '../../shared/BackLink';
import { CharCount } from '../../shared/CharCount';
import { formatDay } from '../../shared/dates';
import { useExceptionDetail, useExceptionAction, type ExceptionAction } from './useExceptions';
import { ApprovalChainBuilder, chainReady, toChainInput, type ChainDraft } from './ApprovalChainBuilder';
import {
  CHAIN_ROLE_LABEL, eventLabel, exceptionActions, OUTCOME_HINT, OUTCOME_LABEL, REVIEW_OUTCOMES,
  REASON_LABEL, RISK_LABEL, RISK_LEVELS, RISK_TONE, STATUS_LABEL, STATUS_TONE, TYPE_LABEL,
  type ExceptionRecord, type ReviewOutcome, type RiskLevel,
} from './types';
import styles from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import own from './Exceptions.module.css';

type Panel = 'submit' | 'approve' | 'reject' | 'recall' | 'activate' | 'revoke' | 'review' | 'renew' | 'mismatch' | 'delete' | null;

export const DONE_MESSAGE: Record<ExceptionAction['name'], string> = {
  submitExceptionForApproval: 'Submitted. The first approver has it now.',
  recallException: 'Recalled and returned to draft.',
  approveExceptionStep: 'Your approval is recorded.',
  rejectExceptionStep: 'Rejected. The requester has been told.',
  activateException: 'Activated. The exception is in force.',
  revokeException: 'Revoked. The exception has ended.',
  initiateExceptionReview: 'Review started.',
  completeExceptionReview: 'Review recorded.',
  requestExceptionRenewal: 'Renewal requested. Set the new expiration and approvers to submit it.',
  submitExceptionRenewal: 'Renewal submitted for approval.',
  acknowledgeVersionMismatch: 'Policy change acknowledged.',
  deleteException: 'Deleted.',
};

function Narrative({ title, text }: { title: string; text: string | null }) {
  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      {text ? <p className={own.prose}>{text}</p> : <p className={`${own.prose} ${own.empty}`}>Not recorded.</p>}
    </section>
  );
}

function Fact({ term, value, children }: { term: string; value?: string | null; children?: React.ReactNode }) {
  return (
    <div>
      <dt className={styles.factTerm}>{term}</dt>
      <dd className={`${styles.factValue} ${value === null ? styles.absent : ''}`}>{children ?? value ?? '—'}</dd>
    </div>
  );
}

export function ExceptionDetailPage() {
  const { exceptionId = '' } = useParams();
  const navigate = useNavigate();
  const { session } = useAuth();
  const orgId = session?.orgId ?? null;
  const roles = session?.roles ?? [];
  const uid = session?.uid ?? null;

  const detail = useExceptionDetail(orgId, exceptionId);
  const act = useExceptionAction(orgId, exceptionId);
  const departments = useTaxonomy('department', Boolean(orgId));
  const members = useMembers(orgId);

  const [panel, setPanel] = useState<Panel>(null);
  const [chain, setChain] = useState<ChainDraft[]>([]);
  const [comment, setComment] = useState('');
  const [outcome, setOutcome] = useState<ReviewOutcome>('continued');
  const [findings, setFindings] = useState('');
  const [conditions, setConditions] = useState('');
  const [newRisk, setNewRisk] = useState<RiskLevel | ''>('');
  const [newExpiry, setNewExpiry] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const ids = useId();
  const id = (n: string) => `${ids}-${n}`;

  const departmentName = useMemo(() => {
    const byId = new Map((departments.data ?? []).map((d) => [d.id, d.name]));
    return (did: string) => byId.get(did) ?? did;
  }, [departments.data]);

  function open(p: Panel) {
    setPanel(panel === p ? null : p);
    setComment(''); setFindings(''); setConditions(''); setNewRisk(''); setNewExpiry('');
  }

  function run(action: ExceptionAction) {
    setDone(null);
    act.mutate(action, {
      onSuccess: () => {
        if (action.name === 'deleteException') { navigate('/exceptions'); return; }
        setDone(DONE_MESSAGE[action.name]);
        setPanel(null);
        setChain([]);
      },
    });
  }

  if (detail.isLoading) return <Spinner label="Opening the exception" />;
  if (detail.error || !detail.data) {
    const notFound = isCallFailure(detail.error) && detail.error.code === 'not-found';
    return (
      <div className={styles.body}>
        <PageState title={notFound ? 'That exception is not here' : 'Could not open the exception'}
          action={{ to: '/exceptions', label: 'Back to exceptions' }} tone="alert">
          {notFound ? 'It may have been deleted, or the link may be wrong.'
            : isCallFailure(detail.error) ? detail.error.message : 'Something went wrong.'}
        </PageState>
      </div>
    );
  }

  const { exception: exc, versions, events, reviews } = detail.data;
  const can = exceptionActions(exc, roles, uid);
  const busy = act.isPending;
  const step = exc.approvalChain[exc.currentApprovalStep];
  const people = members.data ?? [];

  const submitChain = (e: FormEvent) => {
    e.preventDefault();
    if (!chainReady(exc.riskLevel, chain)) return;
    run({ name: 'submitExceptionForApproval', approvalChain: toChainInput(chain, people) });
  };
  const submitRenewal = (e: FormEvent) => {
    e.preventDefault();
    if (!chainReady(exc.riskLevel, chain) || !newExpiry) return;
    run({ name: 'submitExceptionRenewal', approvalChain: toChainInput(chain, people), expirationDate: newExpiry });
  };
  const submitReview = (e: FormEvent) => {
    e.preventDefault();
    run({ name: 'completeExceptionReview', outcome, findings, conditions, newRiskLevel: newRisk || null });
  };

  return (
    <>
      <header className={styles.header}>
        <div className={styles.headerRow}>
          <div className={styles.titled}>
            <p className={styles.eyebrow}>{TYPE_LABEL[exc.exceptionType]} · Version {exc.version}</p>
            <div className={styles.titleLine}>
              <BackLink to="/exceptions" label="Back to exceptions" />
              <h1 className={styles.title}>{exc.title}</h1>
            </div>
            <p className={own.status}>
              <span className={`${styles.tag} ${styles[STATUS_TONE[exc.status]]}`}>{STATUS_LABEL[exc.status]}</span>
              <span className={`${styles.tag} ${styles[RISK_TONE[exc.riskLevel]]}`}>{RISK_LABEL[exc.riskLevel]} risk</span>
              {exc.policyVersionChanged && <span className={`${styles.tag} ${styles.warn}`}>Policy changed</span>}
            </p>
          </div>

          <div className={styles.actions}>
            {can.edit && <Link to={`/exceptions/${exc.id}/edit`} className={styles.btn}>Edit</Link>}
            {can.submit && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'submit'} onClick={() => open('submit')}>Submit for approval…</button>}
            {can.decideStep && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'approve'} onClick={() => open('approve')}>Approve…</button>}
            {can.decideStep && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'reject'} onClick={() => open('reject')}>Reject…</button>}
            {can.recall && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'recall'} onClick={() => open('recall')}>Recall…</button>}
            {can.activate && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'activate'} onClick={() => open('activate')}>Activate…</button>}
            {can.initiateReview && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={() => run({ name: 'initiateExceptionReview' })}>Start review</button>}
            {can.completeReview && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'review'} onClick={() => open('review')}>Complete review…</button>}
            {can.requestRenewal && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={() => run({ name: 'requestExceptionRenewal' })}>Request renewal</button>}
            {can.submitRenewal && <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} aria-expanded={panel === 'renew'} onClick={() => open('renew')}>Submit renewal…</button>}
            {can.acknowledgeMismatch && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'mismatch'} onClick={() => open('mismatch')}>Acknowledge policy change…</button>}
            {can.revoke && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'revoke'} onClick={() => open('revoke')}>Revoke…</button>}
            {can.delete && <button type="button" className={styles.btn} disabled={busy} aria-expanded={panel === 'delete'} onClick={() => open('delete')}>Delete…</button>}
          </div>
        </div>

        {panel === 'submit' && (
          <form className={own.panel} onSubmit={submitChain} aria-labelledby={id('submit-t')}>
            <h2 className={own.panelTitle} id={id('submit-t')}>Who approves this?</h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              Name the people, in order. A {exc.riskLevel}-risk exception has its own rule; it is checked as you build the chain.
            </p>
            <ApprovalChainBuilder orgId={orgId} risk={exc.riskLevel} value={chain} onChange={setChain} idPrefix={id('chain')} />
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !chainReady(exc.riskLevel, chain)}>
                {busy ? 'Submitting…' : 'Submit for approval'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {(panel === 'approve' || panel === 'reject') && step && (
          <form className={own.panel} aria-labelledby={id('decide-t')}
            onSubmit={(e) => { e.preventDefault(); run({ name: panel === 'approve' ? 'approveExceptionStep' : 'rejectExceptionStep', comments: comment }); }}>
            <h2 className={own.panelTitle} id={id('decide-t')}>
              {panel === 'approve' ? 'Approve' : 'Reject'} — step {exc.currentApprovalStep + 1} of {exc.approvalChain.length}, as {CHAIN_ROLE_LABEL[step.role].toLowerCase()}
            </h2>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('comment')}>
                {panel === 'approve' ? 'Comments' : 'Reason'} <span className={form.optional}>(optional)</span>
              </label>
              <textarea id={id('comment')} className={form.textarea} rows={3} maxLength={EXC_COMMENT_MAX}
                value={comment} onChange={(e) => setComment(e.target.value.slice(0, EXC_COMMENT_MAX))} />
              <CharCount value={comment} max={EXC_COMMENT_MAX} />
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
                {busy ? 'Saving…' : panel === 'approve' ? 'Approve this step' : 'Reject the exception'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {panel === 'review' && (
          <form className={own.panel} onSubmit={submitReview} aria-labelledby={id('review-t')}>
            <h2 className={own.panelTitle} id={id('review-t')}>Record the review</h2>
            <fieldset className={form.field} style={{ border: 0, padding: 0, margin: '0 0 var(--sp-5)' }}>
              <legend className={form.label}>Outcome</legend>
              <ul className={own.outcomes}>
                {REVIEW_OUTCOMES.map((o) => (
                  <li key={o}>
                    <label className={own.outcome}>
                      <input type="radio" name={id('outcome')} value={o} checked={outcome === o} onChange={() => setOutcome(o)} />
                      <span><b>{OUTCOME_LABEL[o]}</b><span>{OUTCOME_HINT[o]}</span></span>
                    </label>
                  </li>
                ))}
              </ul>
            </fieldset>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('risk')}>Risk level after this review</label>
              <select id={id('risk')} className={form.select} value={newRisk} onChange={(e) => setNewRisk(e.target.value as RiskLevel | '')}>
                <option value="">Unchanged — {RISK_LABEL[exc.riskLevel]}</option>
                {RISK_LEVELS.filter((r) => r !== exc.riskLevel).map((r) => <option key={r} value={r}>{RISK_LABEL[r]}</option>)}
              </select>
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('findings')}>Findings <span className={form.optional}>(optional)</span></label>
              <textarea id={id('findings')} className={form.textarea} rows={3} maxLength={EXC_ASSESSMENT_MAX}
                value={findings} onChange={(e) => setFindings(e.target.value.slice(0, EXC_ASSESSMENT_MAX))} />
              <CharCount value={findings} max={EXC_ASSESSMENT_MAX} />
            </div>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('conds')}>Updated conditions <span className={form.optional}>(optional)</span></label>
              <textarea id={id('conds')} className={form.textarea} rows={3} maxLength={EXC_ASSESSMENT_MAX}
                value={conditions} onChange={(e) => setConditions(e.target.value.slice(0, EXC_ASSESSMENT_MAX))} />
              <CharCount value={conditions} max={EXC_ASSESSMENT_MAX} />
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? 'Saving…' : 'Record review'}</button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {panel === 'renew' && (
          <form className={own.panel} onSubmit={submitRenewal} aria-labelledby={id('renew-t')}>
            <h2 className={own.panelTitle} id={id('renew-t')}>Renew — renewal {exc.renewalCount + 1}{exc.maxRenewals !== null ? ` of ${exc.maxRenewals}` : ''}</h2>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('expiry')}>New expiration date</label>
              <input id={id('expiry')} className={form.input} type="date" required value={newExpiry} onChange={(e) => setNewExpiry(e.target.value)} />
            </div>
            <p className={form.label}>Who approves the renewal?</p>
            <ApprovalChainBuilder orgId={orgId} risk={exc.riskLevel} value={chain} onChange={setChain} idPrefix={id('rchain')} />
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy || !newExpiry || !chainReady(exc.riskLevel, chain)}>
                {busy ? 'Submitting…' : 'Submit renewal'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {panel === 'mismatch' && (
          <form className={own.panel} aria-labelledby={id('mm-t')}
            onSubmit={(e) => { e.preventDefault(); run({ name: 'acknowledgeVersionMismatch', comments: comment }); }}>
            <h2 className={own.panelTitle} id={id('mm-t')}>The policy has changed since this was approved</h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              Approved against version {exc.policyVersionAtApproval ?? '—'}. Confirm the exception still stands against the current policy, and say why.
            </p>
            <div className={form.field}>
              <label className={form.label} htmlFor={id('mm')}>Comments <span className={form.optional}>(optional)</span></label>
              <textarea id={id('mm')} className={form.textarea} rows={3} maxLength={EXC_COMMENT_MAX}
                value={comment} onChange={(e) => setComment(e.target.value.slice(0, EXC_COMMENT_MAX))} />
              <CharCount value={comment} max={EXC_COMMENT_MAX} />
            </div>
            <div className={styles.actions}>
              <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>{busy ? 'Saving…' : 'Acknowledge'}</button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </form>
        )}

        {(panel === 'recall' || panel === 'activate' || panel === 'revoke' || panel === 'delete') && (
          <div className={own.panel} role="group" aria-labelledby={id('confirm-t')}>
            <h2 className={own.panelTitle} id={id('confirm-t')}>
              {panel === 'recall' && 'Recall this submission?'}
              {panel === 'activate' && 'Put this exception into force?'}
              {panel === 'revoke' && 'Revoke this exception?'}
              {panel === 'delete' && 'Delete this draft?'}
            </h2>
            <p className={styles.subtitle} style={{ marginTop: 0 }}>
              {panel === 'recall' && 'It returns to draft. Approvals given so far are discarded.'}
              {panel === 'activate' && `It becomes active until ${formatDay(exc.expirationDate)}. Staff may practice under it from now.`}
              {panel === 'revoke' && 'It ends now. The record and its history stay as evidence.'}
              {panel === 'delete' && 'A draft nobody has signed. This cannot be undone.'}
            </p>
            <div className={styles.actions}>
              <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}
                onClick={() => run({ name: panel === 'recall' ? 'recallException' : panel === 'activate' ? 'activateException' : panel === 'revoke' ? 'revokeException' : 'deleteException' })}>
                {busy ? 'Working…' : panel === 'recall' ? 'Recall' : panel === 'activate' ? 'Activate' : panel === 'revoke' ? 'Revoke' : 'Delete'}
              </button>
              <button type="button" className={styles.btn} onClick={() => setPanel(null)}>Cancel</button>
            </div>
          </div>
        )}
      </header>

      <div className={styles.body}>
        {done && <div className={`${styles.notice} ${styles.noticeInfo}`} role="status">{done}</div>}
        {act.error && (
          <div className={`${styles.notice} ${styles.noticeDanger}`} role="alert">
            {isCallFailure(act.error) ? act.error.message : 'That could not be done.'}
          </div>
        )}

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Details</h2>
          <dl className={styles.facts}>
            <Fact term="Policy" value={exc.policyId ? undefined : null}>
              {exc.policyId ? (
                <>
                  <Link to={`/policy/${exc.policyId}`}>{exc.policyTitle || exc.policyId}</Link>
                  {exc.policyVersionNumber > 0 && <span className={own.sub}>version {exc.policyVersionNumber}</span>}
                </>
              ) : undefined}
            </Fact>
            <Fact term="Reason" value={REASON_LABEL[exc.reasonCategory]} />
            <Fact term="Oversight" value={exc.oversightCommittee} />
            <Fact term="Requested by" value={exc.requestorDisplayName ?? exc.requestorEmail} />
            <Fact term="Effective" value={exc.effectiveDate ? formatDay(exc.effectiveDate) : null} />
            <Fact term="Expires" value={exc.expirationDate ? formatDay(exc.expirationDate) : null} />
            <Fact term="Review every" value={exc.reviewIntervalDays ? `${exc.reviewIntervalDays} days` : null} />
            <Fact term="Next review" value={exc.nextReviewDate ? formatDay(exc.nextReviewDate) : null} />
            <Fact term="Renewals" value={`${exc.renewalCount}${exc.maxRenewals !== null ? ` of ${exc.maxRenewals}` : ''}`} />
            <Fact term="Last updated" value={exc.updatedAt ? formatDay(exc.updatedAt) : null} />
          </dl>
        </section>

        <Narrative title="Description" text={exc.description} />
        <Narrative title="Justification" text={exc.justification} />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Mitigating controls</h2>
          {exc.mitigatingControls.length ? (
            <ul className={own.bullets}>{exc.mitigatingControls.map((c) => <li key={c}>{c}</li>)}</ul>
          ) : <p className={`${own.prose} ${own.empty}`}>None recorded.</p>}
        </section>

        <Narrative title="Impact assessment" text={exc.impactAssessment} />
        <Narrative title="Conditions to maintain" text={exc.conditions} />

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Affected departments</h2>
          {exc.affectedDepartmentIds.length ? (
            <ul className={styles.chips} style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {exc.affectedDepartmentIds.map((d) => <li key={d} className={styles.chip}>{departmentName(d)}</li>)}
            </ul>
          ) : <p className={`${own.prose} ${own.empty}`}>None recorded.</p>}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Approval chain</h2>
          {exc.approvalChain.length === 0 ? (
            <p className={`${own.prose} ${own.empty}`}>Not yet submitted. The chain is named at submission.</p>
          ) : (
            <ol className={own.chain}>
              {exc.approvalChain.map((s, i) => (
                <li key={i} className={`${own.chainStep} ${exc.status === 'pending_approval' && i === exc.currentApprovalStep ? own.stepNow : ''}`}>
                  <span className={own.stepNo}>{i + 1}</span>
                  <div className={own.stepBody}>
                    <p className={own.stepWho}>{s.approverDisplayName ?? s.approverEmail ?? s.approverUid}</p>
                    <span className={own.stepMeta}>
                      {CHAIN_ROLE_LABEL[s.role]}
                      {s.decision ? ` · ${s.decision === 'approved' ? 'Approved' : 'Rejected'}${s.decidedAt ? ` ${formatDay(s.decidedAt)}` : ''}`
                        : exc.status === 'pending_approval' && i === exc.currentApprovalStep ? ' · Waiting on this step' : ' · Not yet'}
                    </span>
                    {s.comments && <span className={own.stepNote}>{s.comments}</span>}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Reviews</h2>
          {reviews.length === 0 ? (
            <p className={`${own.prose} ${own.empty}`}>No reviews yet.</p>
          ) : (
            <ul className={own.timeline}>
              {reviews.map((r) => (
                <li key={r.id}>
                  <span className={own.when}>{formatDay(r.reviewDate)}</span>
                  <p className={own.what}>
                    {OUTCOME_LABEL[r.outcome]}
                    {r.riskLevelChanged && r.newRiskLevel && ` · risk ${RISK_LABEL[r.riskLevelAtReview]} → ${RISK_LABEL[r.newRiskLevel]}`}
                    {r.reviewerEmail && <span className={own.who}>{r.reviewerEmail}</span>}
                    {r.findings && <span className={own.note}>{r.findings}</span>}
                    {r.conditions && <span className={own.note}>Conditions: {r.conditions}</span>}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Version history</h2>
          {versions.length === 0 ? (
            <p className={`${own.prose} ${own.empty}`}>No snapshots yet. One is kept at each approval and renewal.</p>
          ) : (
            <ul className={own.timeline}>
              {versions.map((v) => (
                <li key={v.id}>
                  <span className={own.when}>{formatDay(v.createdAt)}</span>
                  <p className={own.what}>Version {v.version}<span className={own.who}>{v.trigger.replace(/_/g, ' ')}</span></p>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Activity</h2>
          {events.length === 0 ? (
            <p className={`${own.prose} ${own.empty}`}>Nothing recorded yet.</p>
          ) : (
            <ul className={own.timeline}>
              {events.map((e) => (
                <li key={e.id}>
                  <span className={own.when}>{formatDay(e.createdAt)}</span>
                  <p className={own.what}>
                    {eventLabel(e.action)}
                    {e.actorEmail && <span className={own.who}>{e.actorEmail}</span>}
                    {typeof e.details.comments === 'string' && e.details.comments && <span className={own.note}>{e.details.comments}</span>}
                    {typeof e.details.reason === 'string' && e.details.reason && <span className={own.note}>{e.details.reason}</span>}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}

export type { ExceptionRecord };
